import {
  AlertCircle,
  Brain,
  ChevronRight,
  Crown,
  FileText,
  LifeBuoy,
  LogIn,
  LogOut,
  Settings,
} from 'lucide-react';
import { initDataUser, useSignal } from '@telegram-apps/sdk-react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Card } from '@heroui/react';
import { OPERATOR } from 'legal';
import { useCurrentUser } from '@/entities/user';
import { PageLayout, SettingsGroup, SettingsRow } from '@/shared/ui';
import { ROUTES } from '@/shared/config';
import { useInDesktopShell } from '@/shared/lib/desktopLayout';
import { isTelegram } from '@/shared/lib/platform';
import { openTelegram } from '@/shared/lib/telegram';
import { logout } from '@/shared/lib/webSession';

type RowIcon = typeof AlertCircle;

interface ProfileRow {
  icon: RowIcon;
  /** Фон цветного квадрата — та же iOS-палитра, что у строк в «Настройках». */
  iconClassName: string;
  labelKey: string;
  /** Есть куда вести — строка кликабельна. Нет — заглушка на будущее. */
  to?: string;
}

// Достижения скрыты, пока под них нет экрана.
// «Ошибки» ведёт на тот же разбор, что и таб «Мои ошибки»
// (см. `pages/mistakes`) — это один и тот же экран, вход в него просто
// продублирован. «Словарь» и «Статистика» — отдельные вкладки (см. `TabBar`),
// второй вход отсюда был бы дублем.
const LEARNING_ROWS: ProfileRow[] = [
  { icon: AlertCircle, iconClassName: 'bg-[#ef4444]', labelKey: 'mistakes', to: ROUTES.mistakes },
  {
    icon: Brain,
    iconClassName: 'bg-[#8b5cf6]',
    labelKey: 'personalization',
    to: ROUTES.personalization,
  },
];

/** Вкладка «Профиль»: имя и аватар берём из Telegram, редактировать их в приложении негде и не нужно. */
export function ProfilePage() {
  const { t } = useTranslation('ProfilePage');
  const navigate = useNavigate();
  const telegramUser = useSignal(initDataUser);
  // `/me` уже в кеше с запуска. Пока не пришёл — баннера нет: показать его
  // премиуму и тут же спрятать хуже, чем показать на кадр позже.
  const { user } = useCurrentUser();
  // На компьютере «Выйти» — справа от колонки (`DesktopShell`), второй раз в списке не нужен.
  const inDesktopShell = useInDesktopShell();
  // В браузере данных Telegram нет — имя из онбординга («как обращаться»).
  const displayName =
    [telegramUser?.first_name, telegramUser?.last_name].filter(Boolean).join(' ') ||
    user?.displayName ||
    t('noName');
  const username = telegramUser?.username ? `@${telegramUser.username}` : null;
  // Гость веб-версии (ленивая регистрация, WEB.md): сначала — войти, премиум
  // он всё равно оформит только после входа.
  const guest = user?.isGuest ?? false;
  const showPremium = user != null && !user.isPremium && !guest;

  return (
    <PageLayout title={t('title')}>
      <div className="flex flex-col gap-5">
        <Card className="flex-row items-center gap-3 p-4">
          <Avatar photoUrl={telegramUser?.photo_url} name={displayName} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-base font-bold text-ink">{displayName}</span>
            {username && <span className="truncate text-sm text-ink-muted">{username}</span>}
          </div>
        </Card>

        {guest && (
          <button
            type="button"
            onClick={() =>
              void navigate(ROUTES.login, { state: { reason: 'save' } })
            }
            className="flex items-center gap-3 rounded-3xl bg-signal-blue p-4 text-left text-white shadow-lg transition active:brightness-95"
          >
            <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-white/20">
              <LogIn className="size-6" aria-hidden="true" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-base font-bold">{t('guestTitle')}</span>
              <span className="text-sm text-white/85">{t('guestSubtitle')}</span>
            </span>
            <ChevronRight className="size-5 shrink-0 text-white/80" aria-hidden="true" />
          </button>
        )}

        {showPremium && (
          <button
            type="button"
            onClick={() => void navigate(ROUTES.premium)}
            className="flex items-center gap-3 rounded-3xl bg-gradient-to-r from-[#ff4f9a] to-[#ff7a59] p-4 text-left text-white shadow-lg transition active:brightness-95"
          >
            <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-white/20">
              <Crown className="size-6" aria-hidden="true" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-base font-bold">{t('premiumTitle')}</span>
              <span className="text-sm text-white/85">{t('premiumSubtitle')}</span>
            </span>
            <ChevronRight className="size-5 shrink-0 text-white/80" aria-hidden="true" />
          </button>
        )}

        {/* Подзаголовок «Обучение» пока скрыт. */}
        <SettingsGroup>
          {LEARNING_ROWS.map(({ icon, iconClassName, labelKey, to }) => (
            <SettingsRow
              key={labelKey}
              icon={icon}
              iconClassName={iconClassName}
              label={t(labelKey)}
              disabled={!to}
              onClick={to ? () => void navigate(to) : undefined}
            />
          ))}
        </SettingsGroup>

        <SettingsGroup>
          {/* «Подписка» видна всегда, но без премиума неактивна: купить — через
              баннер «Премиум» выше, а здесь управлять уже купленной. */}
          <SettingsRow
            icon={Crown}
            iconClassName="bg-[#f97316]"
            label={t('subscription')}
            disabled={!user?.isPremium}
            onClick={user?.isPremium ? () => void navigate(ROUTES.subscription) : undefined}
          />
          <SettingsRow
            icon={Settings}
            iconClassName="bg-[#64748b]"
            label={t('settings')}
            onClick={() => void navigate(ROUTES.settings)}
          />
        </SettingsGroup>

        <SettingsGroup>
          {/* Тот же контакт, что в «Документах» в разделе «Связь» — один источник в `legal`. */}
          <SettingsRow
            icon={LifeBuoy}
            iconClassName="bg-[#22c55e]"
            label={t('support')}
            onClick={() => openTelegram(OPERATOR.contact.href)}
          />
          <SettingsRow
            icon={FileText}
            iconClassName="bg-[#0ea5e9]"
            label={t('documents')}
            onClick={() => void navigate(ROUTES.documents)}
          />
        </SettingsGroup>

        {/* Выход — только в браузере: в мини-аппе вход — это сам Telegram.
            Подключение Telegram — в «Настройках». */}
        {/* Гостю выходить некуда: выход стёр бы его прогресс. Пока `/me` не
            пришёл, не знаем, гость ли, — строки нет. */}
        {!isTelegram() && !inDesktopShell && user != null && !guest && (
          <SettingsGroup>
            <SettingsRow
              icon={LogOut}
              iconClassName="bg-[#94a3b8]"
              label={t('logout')}
              onClick={() => void logout()}
            />
          </SettingsGroup>
        )}
      </div>
    </PageLayout>
  );
}

function Avatar({ photoUrl, name }: { photoUrl?: string; name: string }) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        className="size-14 shrink-0 rounded-full border border-hairline object-cover"
      />
    );
  }

  // Вне Telegram (dev) или пока клиент не отдал фото — инициал на подложке.
  return (
    <div className="flex size-14 shrink-0 items-center justify-center rounded-full border border-hairline bg-glass-2 text-lg font-bold text-ink-muted">
      {name.trim().charAt(0).toUpperCase() || '?'}
    </div>
  );
}
